import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Heart, Clock, Activity, Sparkles, ArrowRight } from "lucide-react";
import api from "../lib/api";

const STATUS_COLORS = {
  Excelente: "text-emerald-600 bg-emerald-50",
  Estable: "text-emerald-700 bg-emerald-50",
  Vigilancia: "text-amber-700 bg-amber-50",
  Riesgo: "text-red-700 bg-red-50",
  Bajo: "text-emerald-700 bg-emerald-50",
  Moderado: "text-amber-700 bg-amber-50",
  Alto: "text-orange-700 bg-orange-50",
  "Crítico": "text-red-700 bg-red-50",
  "Muy holgado": "text-emerald-700 bg-emerald-50",
  Holgado: "text-emerald-700 bg-emerald-50",
  Ajustado: "text-amber-700 bg-amber-50",
  Precario: "text-orange-700 bg-orange-50",
};

function Ring({ value, size = 72, color = "#2563eb" }) {
  const r = (size - 8) / 2;
  const c = 2 * Math.PI * r;
  const off = c - (Math.min(100, Math.max(0, value)) / 100) * c;
  return (
    <svg width={size} height={size} className="shrink-0">
      <circle cx={size / 2} cy={size / 2} r={r} stroke="currentColor" strokeWidth="6" fill="none" className="text-secondary" />
      <circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth="6" fill="none" strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round" transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="50%" dominantBaseline="central" textAnchor="middle" className="font-bold tabular" fontSize="18" fill="currentColor">{Math.round(value)}</text>
    </svg>
  );
}

export default function CopilotoWidget() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/copiloto/snapshot").then((r) => setData(r.data)).catch(() => setData({})); }, []);

  if (!data) return <div className="border border-border rounded-lg p-6 text-sm text-muted-foreground">Cargando copiloto…</div>;
  const health = data.health || {};
  const horizon = data.horizon || {};
  const stress = data.stress || {};

  const healthColor = health.score >= 80 ? "#10b981" : health.score >= 60 ? "#059669" : health.score >= 40 ? "#f59e0b" : "#dc2626";
  const stressColor = stress.score < 20 ? "#10b981" : stress.score < 45 ? "#f59e0b" : stress.score < 70 ? "#f97316" : "#dc2626";

  return (
    <div className="border border-border rounded-2xl p-5 bg-gradient-to-br from-primary/5 via-transparent to-transparent" data-testid="copiloto-widget">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-bold flex items-center gap-1.5">
            <Sparkles size={12} /> Copiloto Financiero
          </div>
          <div className="font-heading font-extrabold text-lg mt-0.5">Cómo estás económicamente</div>
        </div>
        <Link to="/copiloto" data-testid="copiloto-open" className="text-xs font-semibold text-primary hover:underline flex items-center gap-1">
          Analizar decisión <ArrowRight size={12} />
        </Link>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="flex items-center gap-3 bg-background/60 rounded-xl p-3 border border-border/60" data-testid="widget-health">
          <Ring value={health.score || 0} color={healthColor} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1"><Heart size={10} /> Salud</div>
            <div className={`text-xs font-bold mt-0.5 inline-block px-1.5 py-0.5 rounded ${STATUS_COLORS[health.status] || "bg-secondary"}`}>{health.status || "—"}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-background/60 rounded-xl p-3 border border-border/60" data-testid="widget-horizon">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex flex-col items-center justify-center shrink-0">
            <Clock size={16} className="text-primary" />
            <span className="text-[10px] font-bold text-primary mt-0.5">{horizon.months ? `${horizon.months.toFixed(1)}m` : "—"}</span>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Horizonte</div>
            <div className="text-sm font-semibold truncate">{horizon.display || "—"}</div>
            <div className={`text-[10px] font-bold mt-0.5 inline-block px-1.5 py-0.5 rounded ${STATUS_COLORS[horizon.status] || "bg-secondary"}`}>{horizon.status || "—"}</div>
          </div>
        </div>

        <div className="flex items-center gap-3 bg-background/60 rounded-xl p-3 border border-border/60" data-testid="widget-stress">
          <Ring value={stress.score || 0} color={stressColor} />
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold flex items-center gap-1"><Activity size={10} /> Estrés</div>
            <div className={`text-xs font-bold mt-0.5 inline-block px-1.5 py-0.5 rounded ${STATUS_COLORS[stress.level] || "bg-secondary"}`}>{stress.level || "—"}</div>
            {stress.factors && stress.factors.length > 0 && (
              <div className="text-[10px] text-muted-foreground mt-0.5 truncate" title={stress.factors.join(" · ")}>{stress.factors[0]}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
